import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  AuthAlert,
  AuthField,
  AuthInput,
  AuthPageLayout,
  AuthSubmitButton,
  authStyles,
} from '../../auth/components/AuthPageLayout';
import { getBaseDomain } from '../../../shared/config/workspaceHost';
import {
  checkWorkspaceSlug,
  createWorkspace,
  readPickerSession,
} from '../services/workspaceService';
import { followWorkspaceSelection } from '../workspaceNavigation';
import styles from './WorkspacePicker.module.css';

const SLUG_DEBOUNCE_MS = 400;

/**
 * Turns arbitrary text into a plausible slug. It is only a starting suggestion: the owner confirms
 * the value, and the server has the final say on format, reserved names, and uniqueness.
 */
const suggestSlug = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);

type SlugState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'available' }
  | { status: 'unavailable'; reason: string };

export const CreateWorkspacePage: React.FC = () => {
  const { t } = useTranslation('workspaces');
  const navigate = useNavigate();
  const session = React.useMemo(() => readPickerSession(), []);
  const accountToken = session?.accountToken ?? null;

  const [name, setName] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [slugEdited, setSlugEdited] = React.useState(false);
  const [slugState, setSlugState] = React.useState<SlugState>({ status: 'idle' });
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const effectiveSlug = slugEdited ? slug : suggestSlug(name);

  React.useEffect(() => {
    if (!accountToken || effectiveSlug.length < 2) {
      setSlugState({ status: 'idle' });
      return undefined;
    }

    setSlugState({ status: 'checking' });

    let cancelled = false;
    const timer = window.setTimeout(() => {
      checkWorkspaceSlug(accountToken, effectiveSlug)
        .then(result => {
          if (cancelled) {
            return;
          }

          setSlugState(
            result.available
              ? { status: 'available' }
              : { status: 'unavailable', reason: result.reason ?? t('create.slugUnavailable') },
          );
        })
        .catch(() => {
          if (!cancelled) {
            // A failed check must not block the attempt; the server validates again on submit.
            setSlugState({ status: 'idle' });
          }
        });
    }, SLUG_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [accountToken, effectiveSlug, t]);

  if (!accountToken) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t('create.nameRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      const created = await createWorkspace(accountToken, name.trim(), effectiveSlug);
      followWorkspaceSelection(created, navigate, { startSetup: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.createFailed'));
      setIsSubmitting(false);
    }
  };

  const baseDomain = getBaseDomain();
  const host = baseDomain
    ? `${effectiveSlug || t('create.slugPlaceholder')}.${baseDomain}`
    : null;

  return (
    <AuthPageLayout title={t('create.title')} subtitle={t('create.subtitle')}>
      <form className={authStyles.form} onSubmit={onSubmit}>
        {error ? <AuthAlert variant="error">{error}</AuthAlert> : null}

        <AuthField label={t('create.nameLabel')} htmlFor="workspace-name" icon="mail">
          <AuthInput
            id="workspace-name"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder={t('create.namePlaceholder')}
          />
        </AuthField>

        <AuthField label={t('create.slugLabel')} htmlFor="workspace-slug" icon="mail">
          <AuthInput
            id="workspace-slug"
            value={effectiveSlug}
            onChange={event => {
              setSlugEdited(true);
              setSlug(suggestSlug(event.target.value));
            }}
            placeholder={t('create.slugPlaceholder')}
          />
        </AuthField>

        {host ? <p className={styles.status}>{host}</p> : null}

        {slugState.status === 'checking' ? (
          <p className={styles.status}>{t('create.slugChecking')}</p>
        ) : null}
        {slugState.status === 'available' ? (
          <p className={styles.status}>{t('create.slugAvailable')}</p>
        ) : null}
        {slugState.status === 'unavailable' ? (
          <AuthAlert variant="error">{slugState.reason}</AuthAlert>
        ) : null}

        <AuthSubmitButton
          loading={isSubmitting}
          loadingLabel={t('create.submitting')}
          disabled={slugState.status === 'unavailable'}
        >
          {t('create.submit')}
        </AuthSubmitButton>
      </form>
    </AuthPageLayout>
  );
};

export default CreateWorkspacePage;
